import { apiRequest } from 'js-api-client';

/**
 * Gontijo's client-auth endpoints, as platform-purchase exposes them.
 *
 * The funnel never talks to Gontijo's ERP. It talks to platform-purchase, which holds the carrier
 * credentials and forwards the call — attaching `GTJ-CLI-Authorization` itself on the way out. So
 * everything here is an ordinary purchase-API call, and the `client_auth` that comes back is a
 * value to carry, not a header to send.
 */
const VALIDATE_TOKEN_ENDPOINT = 'gontijo/auth/validate_token';
const CLIENT_DETAILS_ENDPOINT = 'gontijo/auth/client_details';

const BAD_REQUEST = 400;
const UNAUTHORIZED = 401;
const SERVER_ERROR = 500;

/**
 * Whether a refusal came from the API-key gate rather than from the session or the password.
 *
 * The most common failure of this funnel is an unregistered `Origin`, which answers 401 on *any*
 * call. Read as "your session expired", a tenant misconfiguration signs every buyer out, over and
 * over, and the real cause never appears anywhere. The bodies tell them apart: the gate renders an
 * `errors` array (`ApiHelpers#unauthorized`), a rejected session renders `error` or `reason`.
 *
 * Worth saying out loud because the auth routes make it easy to get backwards: bad credentials
 * there come back as **422**, not 401 (`auth_controller.rb:96`). A 401 on `/gontijo/auth/*` is
 * almost always the gate.
 */
function isGateRejection(status: number | null, body: unknown): boolean {
  if (status !== UNAUTHORIZED) return false;

  return Array.isArray((body as { errors?: unknown })?.errors);
}

/** The body an error carries, wherever the client happened to hang it. */
function readErrorBody(error: unknown): unknown {
  return (error as { details?: unknown })?.details ?? null;
}

/**
 * The HTTP status off a client error.
 *
 * With `skipErrorMapping` the client puts the real status in `code`; without it, 404 and 500 are
 * rewritten to 100 and 101, which is exactly the remapping these calls opt out of. Null means the
 * request never completed, which is a different thing from any status and is treated as one.
 */
function readErrorStatus(error: unknown): number | null {
  const code = (error as { code?: unknown })?.code;

  return typeof code === 'number' && code >= 100 && code < 600 ? code : null;
}

function postToAuthRoute(endpoint: string, payload: Record<string, string>): Promise<unknown> {
  // `skipErrorMapping` on purpose: these calls need the real HTTP status. The legacy mapping turns
  // 404 into 100 and 500 into 101 and folds the body into a business-error shape, which would erase
  // the exact distinctions the session verdict is built on — 401 from 422, and 5xx from both.
  return apiRequest.post(endpoint, payload, { apiVersion: 'v2', skipErrorMapping: true });
}

/**
 * What the backend says about a stored token.
 *
 * Three values rather than a boolean, because the endpoint distinguishes three things and only one
 * of them is a reason to sign somebody out. `unreachable` means "we could not ask" — it is not a
 * no, and treating it as one logs every buyer out whenever the carrier has a bad minute.
 */
interface GontijoSessionVerdict {
  valid: boolean;
  expired: boolean;
  unreachable: boolean;
}

/**
 * Asks whether a token is still good. Never throws: every outcome is one of the three verdicts.
 *
 * The route answers 200 `{valid: true}`, 401 for a token it turned down, 400 for a malformed
 * `client_auth`, and 502 only when the provider itself failed — never 403 or 404, and never a 200
 * saying `false`. So a 5xx or no answer at all is the only "could not ask"; every other status is
 * the backend saying something about the session.
 *
 * The 400 is a rejection as final as the 401: a stored token the backend cannot parse will never
 * pay, and keeping it would leave a session that reads as signed in and fails at the charge. The
 * gate's 401 is the one refusal that stays `unreachable`.
 */
async function askGontijoTokenVerdict(token: string): Promise<GontijoSessionVerdict> {
  try {
    await postToAuthRoute(VALIDATE_TOKEN_ENDPOINT, { client_auth: token });

    return { valid: true, expired: false, unreachable: false };
  } catch (error) {
    const status = readErrorStatus(error);
    const body = readErrorBody(error);

    // The gate's 401 is about the deployment, not about this token. Reading it as a dead session is
    // how an unregistered Origin signs everybody out and leaves nothing behind saying why.
    if (isGateRejection(status, body)) return { valid: false, expired: false, unreachable: true };

    return {
      valid: false,
      expired: status === UNAUTHORIZED || status === BAD_REQUEST,
      unreachable: status === null || status >= SERVER_ERROR,
    };
  }
}

/**
 * The account holder's record, as the passengers form needs it.
 *
 * `documentType` is the provider's `dscrTipoDocmClie`, which platform-purchase does not forward yet:
 * it is a claim to check when it arrives, never a field to count on. `phone` is empty when the
 * carrier holds no mobile — the endpoint omits the key rather than sending it blank. `email` and
 * `birthDate` are the ERP's `mailClie` and `dataNascClie`, read only once the route forwards them.
 */
interface GontijoAccountDetails {
  name: string;
  documentNumber: string;
  documentType?: string;
  phone: string;
  email?: string;
  birthDate?: string;
}

/** A trimmed string, or empty for anything that is not one. */
function readText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * The record behind a token, or null when there is none worth using.
 *
 * Null covers every failure alike — a rejected session, a gate refusal, a provider error — and a
 * record missing its name or its document, which is useless as a prefill. None of these is this
 * call's business to act on: whether the session is still alive is `validate_token`'s verdict, and
 * a missing offer is all the buyer loses. Snake case on purpose: `js-api-client` hands the body back
 * exactly as Rails rendered it.
 */
async function fetchGontijoClientDetails(token: string): Promise<GontijoAccountDetails | null> {
  try {
    const response = (await postToAuthRoute(CLIENT_DETAILS_ENDPOINT, { client_auth: token })) as {
      client?: Record<string, unknown>;
    } | null;
    const client = response?.client;

    const name = readText(client?.name);
    const documentNumber = readText(client?.document_number);
    if (!name || !documentNumber) return null;

    const documentType = readText(client?.document_type);
    const email = readText(client?.email);
    const birthDate = readText(client?.birth_date);

    return {
      name,
      documentNumber,
      ...(documentType && { documentType }),
      phone: readText(client?.phone_number),
      ...(email && { email }),
      ...(birthDate && { birthDate }),
    };
  } catch {
    return null;
  }
}

export { askGontijoTokenVerdict, fetchGontijoClientDetails, isGateRejection };
export type { GontijoAccountDetails, GontijoSessionVerdict };
